import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Card, CardContent, Switch, FormControlLabel, Divider } from '@mui/material';
import { track, trackClick, trackSearch, trackForm } from '../utils/mixpanelTracking';
import MixpanelTrackingWrapper from './MixpanelTrackingWrapper';
import useMixpanelTracking from '../hooks/useMixpanelTracking';
import mixpanelScreenRecording from '../utils/mixpanelScreenRecording';

/**
 * 🎯 Enhanced Mixpanel Example - With Autocapture and Screen Recording
 */
const SimpleExample = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingId, setRecordingId] = useState(null);
  
  // Use our custom hook for tracking
  const { trackEvent, trackInteraction, createTrackingHandler } = useMixpanelTracking({
    componentName: 'MixpanelDemo',
    properties: { demo_version: '2.0' },
    trackMount: true,
    trackUnmount: true
  });

  // Example: Track when component loads
  useEffect(() => {
    track('Enhanced Example Viewed', { 
      component: 'demo',
      features: ['autocapture', 'screen_recording', 'heatmap', 'session_tracking']
    });
    
    // Simulate loading data
    setTimeout(() => {
      setData([1, 2, 3, 4, 5]);
      trackEvent('Data Loaded', { record_count: 5, load_time: 1000 });
    }, 1000);
  }, [trackEvent]);

  // Example: Track button click (just add ONE line)
  const handleRefresh = () => {
    trackClick('refresh_data', { current_count: data.length }); // ← ADD THIS LINE
    // Your existing refresh logic here
    setData([...data, data.length + 1]);
  };

  // Example: Track search (just add ONE line)
  const handleSearch = (e) => {
    e.preventDefault();
    trackSearch(searchTerm, data); // ← ADD THIS LINE
    // Your existing search logic here
    console.log('Searching for:', searchTerm);
  };

  // Example: Track form submit (just add ONE line)
  const handleForm = (e) => {
    e.preventDefault();
    trackForm('example_form', { search_term: searchTerm }); // ← ADD THIS LINE
    // Your existing form logic here
    alert('Form submitted!');
  };

  // Toggle screen recording
  const toggleRecording = () => {
    if (isRecording) {
      const data = mixpanelScreenRecording.stopScreenRecording();
      setIsRecording(false);
      trackEvent('Screen Recording Stopped', { recording_id: recordingId, duration: data.duration_ms });
    } else {
      const newRecordingId = mixpanelScreenRecording.startScreenRecording();
      setRecordingId(newRecordingId);
      setIsRecording(true);
      trackEvent('Screen Recording Started', { recording_id: newRecordingId });
    }
  };

  // Create a tracking-enhanced button click handler
  const enhancedRefreshHandler = createTrackingHandler(
    'Enhanced Refresh Clicked',
    handleRefresh,
    { method: 'enhanced', current_count: data.length }
  );

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        🎯 Enhanced Mixpanel Tracking Demo
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        This demonstrates all Mixpanel features including Autocapture, Screen Recording, and Session Tracking.
        Check browser console to see tracking events.
      </Typography>

      {/* Screen Recording Controls */}
      <Card sx={{ mb: 3, bgcolor: isRecording ? 'rgba(255, 0, 0, 0.05)' : 'inherit' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Screen Recording
          </Typography>
          <FormControlLabel
            control={
              <Switch 
                checked={isRecording}
                onChange={toggleRecording}
                color="primary"
              />
            }
            label={isRecording ? "Recording Active" : "Start Recording"}
          />
          {isRecording && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              Recording in progress (ID: {recordingId})
            </Typography>
          )}
          <Typography variant="body2" sx={{ mt: 1 }}>
            This feature captures user interactions, console logs, and network requests.
          </Typography>
        </CardContent>
      </Card>

      <Divider sx={{ my: 3 }} />

      {/* Original examples with enhancements */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Button Click Tracking (Multiple Methods)
          </Typography>
          
          {/* Original method */}
          <Button 
            variant="contained" 
            onClick={handleRefresh}
            sx={{ mr: 2 }}
            data-mp-id="refresh-button"
          >
            Standard Tracking ({data.length} items)
          </Button>
          
          {/* Enhanced method with hook */}
          <Button 
            variant="contained" 
            onClick={enhancedRefreshHandler}
            sx={{ mr: 2, ml: 2 }}
            color="secondary"
          >
            Hook-Enhanced Tracking
          </Button>
          
          {/* Component wrapper method */}
          <MixpanelTrackingWrapper 
            eventName="Wrapper Button Clicked"
            properties={{ method: 'wrapper', items: data.length }}
            trackOnClick={true}
            id="wrapper-button"
          >
            <Button 
              variant="outlined" 
              onClick={() => setData([...data, data.length + 1])}
              sx={{ ml: 2 }}
            >
              Wrapper Tracking
            </Button>
          </MixpanelTrackingWrapper>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Search Tracking with Autocapture
          </Typography>
          <form onSubmit={handleSearch} data-mp-id="search-form">
            <TextField
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search something..."
              sx={{ mb: 2 }}
              data-mp-id="search-input"
            />
            <Button type="submit" variant="outlined" data-mp-id="search-button">
              Search
            </Button>
          </form>
          <Typography variant="body2" sx={{ mt: 1 }}>
            This form has data-mp-id attributes for enhanced autocapture tracking
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Form Tracking with Sensitive Data Protection
          </Typography>
          <form onSubmit={handleForm} data-mp-id="sensitive-form">
            <TextField
              fullWidth
              type="password"
              placeholder="Password (masked in recordings)"
              sx={{ mb: 2 }}
              data-mp-mask
            />
            <Button type="submit" variant="outlined" data-mp-track="sensitive-form-submit">
              Submit Form
            </Button>
          </form>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Password field is automatically masked in screen recordings
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          📊 Enhanced Features Now Active:
        </Typography>
        <ul>
          <li><strong>Autocapture</strong> - Automatically tracks clicks, form submissions, and page views</li>
          <li><strong>Screen Recording</strong> - Records user sessions for replay in Mixpanel</li>
          <li><strong>Heatmaps</strong> - Tracks mouse movements to generate click and hover heatmaps</li>
          <li><strong>Session Tracking</strong> - Records user journeys through the application</li>
          <li><strong>Console & Network Monitoring</strong> - Captures errors and network requests</li>
          <li><strong>Privacy Protection</strong> - Automatically masks sensitive data</li>
        </ul>
      </Box>
    </Box>
  );
};

export default SimpleExample;